import { Text, View, hexColor } from "@lightningtv/solid";
import { createSignal } from "solid-js";
import { fontFamily, isRTL, t } from "../../../translations/configuration";
import { useNavigate } from "@solidjs/router";

interface LanguageProps {
  x?: number;
  enLanguage: any;
  languageRef: any;
  itLang: any;
  deLang: any;
  esLang: any;
  frLang: any;
  saLang: any;
  grLang: any;
  cnLang: any;
  trLang: any;
  changeLanguage: (lang: string) => void;
}

const Language = (props: LanguageProps) => {
  let enRef: any, itRef: any, esRef: any, frRef: any, saRef: any;
  let cnRef: any, deRef: any, grRef: any, trRef: any;

  const navigate = useNavigate();
  const [focused, setFocused] = createSignal<string | null>(null);

  const handleLanguageChange = (lang: string) => {
    props.changeLanguage(lang);
    if (lang === "sa") navigate(-1);
  };

  const FLAG_W = 160;
  const FLAG_H = 110;
  const CARD_W = 190;
  const CARD_H = 170;
  const GAP = 20;

  const flagStyle = (lang: string) => ({
    borderRadius: 14,
    width: CARD_W,
    height: CARD_H,
    color: hexColor("#12151c"),
    border: {
      width: focused() === lang ? 3 : 0,
      color: focused() === lang ? hexColor("#2563eb") : hexColor("#00000000"),
    },
    focus: {
      color: hexColor("#12151c"),
      border: { width: 3, color: hexColor("#2563eb") },
    },
    transition: { color: { duration: 150 } },
  });

  // Row 1: English, Italian, Spanish, French, Arabic
  // Row 2: Chinese, German, Greek, Turkish
  return (
    <View x={props.x || 150} y={140} height={2000} width={1100} clipping>

      {/* ── Row 1 ── */}
      <View y={0} style={{ flexDirection: "row" }} gap={GAP}>

        {/* English */}
        <View
          ref={(el) => { enRef = el; if (typeof props.enLanguage === 'function') props.enLanguage(el); }}
          style={flagStyle("en")}
          onFocus={() => setFocused("en")}
          onBlur={() => setFocused(null)}
          onLeft={() => isRTL() ? null : props.languageRef?.setFocus()}
          onRight={() => itRef?.setFocus()}
          onDown={() => cnRef?.setFocus()}
          onEnter={() => handleLanguageChange("en")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/7x245bF6/usa.png" />
          <Text x={55} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("english")}
          </Text>
        </View>

        {/* Italian */}
        <View
          ref={(el) => { itRef = el; if (typeof props.itLang === 'function') props.itLang(el); }}
          x={CARD_W + GAP}
          style={flagStyle("it")}
          onFocus={() => setFocused("it")}
          onBlur={() => setFocused(null)}
          onLeft={() => enRef?.setFocus()}
          onRight={() => esRef?.setFocus()}
          onDown={() => deRef?.setFocus()}
          onEnter={() => handleLanguageChange("it")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/VYD4hq0t/Flag-of-Italy-1946-2003.png" />
          <Text x={55} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("italian")}
          </Text>
        </View>

        {/* Spanish */}
        <View
          ref={(el) => { esRef = el; if (typeof props.esLang === 'function') props.esLang(el); }}
          x={(CARD_W + GAP) * 2}
          style={flagStyle("es")}
          onFocus={() => setFocused("es")}
          onBlur={() => setFocused(null)}
          onLeft={() => itRef?.setFocus()}
          onRight={() => frRef?.setFocus()}
          onDown={() => grRef?.setFocus()}
          onEnter={() => handleLanguageChange("es")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/0Nds6Fx/Spain-flag-300.png" />
          <Text x={52} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("spanish")}
          </Text>
        </View>

        {/* French */}
        <View
          ref={(el) => { frRef = el; if (typeof props.frLang === 'function') props.frLang(el); }}
          x={(CARD_W + GAP) * 3}
          style={flagStyle("fr")}
          onFocus={() => setFocused("fr")}
          onBlur={() => setFocused(null)}
          onLeft={() => esRef?.setFocus()}
          onRight={() => saRef?.setFocus()}
          onDown={() => trRef?.setFocus()}
          onEnter={() => handleLanguageChange("fr")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/Fb5JmryQ/Flag-of-France.png" />
          <Text x={56} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("french")}
          </Text>
        </View>

        {/* Arabic / Saudi */}
        <View
          ref={(el) => { saRef = el; if (typeof props.saLang === 'function') props.saLang(el); }}
          x={(CARD_W + GAP) * 4}
          style={flagStyle("sa")}
          onFocus={() => setFocused("sa")}
          onBlur={() => setFocused(null)}
          onLeft={() => frRef?.setFocus()}
          onRight={() => isRTL() ? props.languageRef?.setFocus() : null}
          onDown={() => trRef?.setFocus()}
          onEnter={() => handleLanguageChange("sa")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/R4hRZZ3W/Flag-of-Saudi-Arabia.jpg" />
          <Text x={52} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("saudiArabia")}
          </Text>
        </View>
      </View>

      {/* ── Row 2 ── */}
      <View y={CARD_H + 40} style={{ flexDirection: "row" }} gap={GAP}>

        {/* Chinese */}
        <View
          ref={(el) => { cnRef = el; }}
          style={flagStyle("cn")}
          onFocus={() => setFocused("cn")}
          onBlur={() => setFocused(null)}
          onUp={() => enRef?.setFocus()}
          onRight={() => deRef?.setFocus()}
          onEnter={() => handleLanguageChange("cn")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/bjGwVC3S/Flag-of-the-People-s-Republic-of-China.jpg" />
          <Text x={50} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("chinese")}
          </Text>
        </View>

        {/* German */}
        <View
          ref={(el) => { deRef = el; if (typeof props.deLang === 'function') props.deLang(el); }}
          x={CARD_W + GAP}
          style={flagStyle("de")}
          onFocus={() => setFocused("de")}
          onBlur={() => setFocused(null)}
          onUp={() => itRef?.setFocus()}
          onLeft={() => cnRef?.setFocus()}
          onRight={() => grRef?.setFocus()}
          onEnter={() => handleLanguageChange("de")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/27mzrq4t/Flag-of-Germany-svg.webp" />
          <Text x={50} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("german")}
          </Text>
        </View>

        {/* Greek */}
        <View
          ref={(el) => { grRef = el; }}
          x={(CARD_W + GAP) * 2}
          style={flagStyle("gr")}
          onFocus={() => setFocused("gr")}
          onBlur={() => setFocused(null)}
          onUp={() => esRef?.setFocus()}
          onLeft={() => deRef?.setFocus()}
          onRight={() => trRef?.setFocus()}
          onEnter={() => handleLanguageChange("gr")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/KcqQRBr4/Flag-of-Greece.jpg" />
          <Text x={58} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("greek")}
          </Text>
        </View>

        {/* Turkish */}
        <View
          ref={(el) => { trRef = el; }}
          x={(CARD_W + GAP) * 3}
          style={flagStyle("tr")}
          onFocus={() => setFocused("tr")}
          onBlur={() => setFocused(null)}
          onUp={() => frRef?.setFocus()}
          onLeft={() => grRef?.setFocus()}
          onEnter={() => handleLanguageChange("tr")}
        >
          <View x={15} y={10} width={FLAG_W} height={FLAG_H} borderRadius={10}
            src="https://i.ibb.co/mFzZGBrS/Flag-of-Turkey.jpg" />
          <Text x={52} y={128} style={{ fontSize: 20, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
            {t("turkish")}
          </Text>
        </View>

      </View>
    </View>
  );
};

export default Language;